import {
	GQLCouponDiscountOnType,
	GQLOrderFragment,
	GQLOrderItemTileFragment,
	GQLOrderStatusType,
	GQLOrderTileFragment,
	GQLPaymentFragment,
	GQLPaymentLineFragment,
	GQLPaymentStatusType,
	GQLSupplementaryPaymentStatusType,
} from '@theprintbar/graphql';
import { getCurrencyValue } from '../../utils';
import { getOrderPricing } from '../orders';

export function getPaymentDiscountPercentage(payment: GQLPaymentFragment) {
	const { total, subtotal } = getPaymentAmount(payment);
	const percentage = subtotal > 0 ? Math.round(((subtotal - total) / subtotal) * 100) : 0;
	return percentage;
}

export function getPaymentAmount(payment: GQLPaymentFragment) {
	const value = getCurrencyValue(payment.lineTotal);
	return {
		subtotal: value,
		total: value - getCurrencyValue(payment.coupon?.value || 0),
	};
}

export function getPaymentLineAmount(line?: GQLPaymentLineFragment | null) {
	return {
		subtotal: getCurrencyValue(line?.subtotal),
		total: getCurrencyValue(line?.total),
	};
}

// Stamped on a payment line when staff mark an order paid outside the system
// ("No payment required", bank transfer, in person). The line is zeroed at the
// same time, and that zero is a deliberate decision — not a computed figure —
// so nothing may recalculate it afterwards.
export const MARKED_PAID_REFERENCE = 'Marked Paid';

export function isPaymentLineMarkedPaid(line?: Pick<GQLPaymentLineFragment, 'reference'> | null) {
	return line?.reference === MARKED_PAID_REFERENCE;
}

export function getRefundedAmount(payment: GQLPaymentFragment, orderId?: string) {
	return getCurrencyValue(
		payment.refunds?.reduce((acc, refund) => {
			if (orderId && refund.order.id !== orderId) return acc;
			return acc + getCurrencyValue(refund.amount);
		}, 0) || 0,
	);
}

export function availableShippingRefund(payment: GQLPaymentFragment, order: GQLOrderFragment) {
	let shippingCharge = getCurrencyValue(order.shippingCharge);

	const dicount = getPaymentDiscountPercentage(payment);

	return !!payment.refunds?.find(refund => refund.includesShipping)
		? 0
		: getCurrencyValue(shippingCharge - shippingCharge * (dicount / 100));
}

export function orderHasSingleItemToRefund(order: GQLOrderFragment, payment?: GQLPaymentFragment) {
	const orderPayment = payment || getOrderPayment(order);
	if (!orderPayment || orderPayment.status !== GQLPaymentStatusType.Complete) return false;

	const itemCount = order.items?.length || 0;
	const refundedItems =
		orderPayment.refunds?.reduce((acc, refund) => {
			if (refund.orderItems?.length) {
				return acc + refund.orderItems.length;
			}
			return acc;
		}, 0) || 0;
	return itemCount - refundedItems <= 1;
}

export function getOrderRefunds(order: GQLOrderTileFragment) {
	return getCurrencyValue(order.paymentRefunds?.reduce((acc, refund) => acc + getCurrencyValue(refund.amount), 0) || 0);
}

export function getAdditionalPaymentAmount(payment: GQLPaymentFragment) {
	return getAdditionalSystemPaymentAmount(payment) + getAdditionalManualPaymentAmount(payment);
}

export function getAdditionalSystemPaymentAmount(payment: GQLPaymentFragment) {
	return payment.supplementaryPayments?.reduce((acc, sp) => (sp.isSystemPayment ? acc + getCurrencyValue(sp.amount) : acc), 0) || 0;
}

export function getAdditionalManualPaymentAmount(payment: GQLPaymentFragment) {
	return payment.supplementaryPayments?.reduce((acc, sp) => (!sp.isSystemPayment ? acc + getCurrencyValue(sp.amount) : acc), 0) || 0;
}

export function getTotalPaymentAmount(payment?: GQLPaymentFragment) {
	if (!payment) return getCurrencyValue(0);

	return getCurrencyValue(getPaymentAmount(payment).total + getAdditionalPaymentAmount(payment));
}

export function getPaidAmount(order: GQLOrderTileFragment, payment: GQLPaymentFragment) {
	if (!payment || payment.status !== GQLPaymentStatusType.Complete) return 0;

	let paid = getPaymentAmount(payment).total;

	payment.supplementaryPayments?.forEach(sp => {
		if (sp.status === GQLSupplementaryPaymentStatusType.Complete) {
			paid += parseFloat(sp.amount);
		}
	});
	const lineTotal = getPaymentLineAmount(order.paymentLine);

	const orderPaid = !!lineTotal?.total && lineTotal.total < paid ? lineTotal.total : paid;

	return getCurrencyValue(orderPaid);
}

export function getOrderPayment(order: GQLOrderFragment) {
	return order.payment || order.paymentLine?.payment;
}

export function getAvailableOrderRefund(order: GQLOrderFragment, orderItem?: GQLOrderItemTileFragment) {
	const payment = getOrderPayment(order);
	if (!payment || payment.status !== GQLPaymentStatusType.Complete || (!payment.powerboardChargeId && !payment.transactionReference)) {
		return {
			availableItemRefund: 0,
			availableShippingRefund: 0,
			totalAvailableRefund: 0,
			singleOrderItemToRefund: false,
		};
	}

	const paidAmount = getPaidAmount(order, payment);
	let shippingRefund = availableShippingRefund(payment, order);
	const refunded = getRefundedAmount(payment, order.id);
	let availableItemRefund = getCurrencyValue(paidAmount - refunded - shippingRefund);

	if (orderItem) {
		const itemTotal = getCurrencyValue(orderItem.total);
		if (itemTotal < availableItemRefund) {
			availableItemRefund = getCurrencyValue(itemTotal);
		}
	}

	return {
		availableItemRefund,
		availableShippingRefund: shippingRefund,
		totalAvailableRefund: getCurrencyValue(availableItemRefund + shippingRefund),
		singleOrderItemToRefund: orderHasSingleItemToRefund(order, payment),
	};
}

export function getAvailableOrderDiscount(order: GQLOrderFragment) {
	if (order.status !== GQLOrderStatusType.PendingPayment) {
		return 0;
	}
	const pricing = getOrderPricing(order);

	return pricing.total + pricing.discount;
}
